/**
 * useOrgMembers — TanStack Query hooks for the organization members panel.
 *
 * BE: ``/api/v1/org/members`` + ``/api/v1/org/invites`` (org_management.py).
 *
 * Cache strategy mirrors ``useOrgBranding``: the data is small and rarely
 * changes, so we hold a generous staleTime and let mutations invalidate.
 * The invites query is gated by an ``enabled`` flag because non-admin
 * callers receive 403 — leaving it always-on would spam the BE with
 * permission failures every time a member opens settings.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  cancelOrgInvite,
  inviteOrgMember,
  type InviteMemberRequest,
  type InviteMemberResponse,
  listOrgInvites,
  listOrgMembers,
  type OrgInvite,
  type OrgMember,
  type OrgRole,
  removeOrgMember,
  updateOrgMemberRole,
} from "@ando/core";

const MEMBERS_KEY = ["settings", "org", "members"] as const;
const INVITES_KEY = ["settings", "org", "invites"] as const;
const STALE_MS = 60_000;

export const orgMembersQueryKeys = {
  members: MEMBERS_KEY,
  invites: INVITES_KEY,
} as const;

export function useOrgMembers() {
  return useQuery<readonly OrgMember[]>({
    queryKey: MEMBERS_KEY,
    queryFn: listOrgMembers,
    staleTime: STALE_MS,
  });
}

export interface UseOrgInvitesOptions {
  /**
   * Only fires the request when the caller is admin/owner — the BE
   * returns 403 for plain members and we'd rather not surface that as
   * a query error in the panel.
   */
  readonly enabled?: boolean;
}

export function useOrgInvites(opts: UseOrgInvitesOptions = {}) {
  return useQuery<readonly OrgInvite[]>({
    queryKey: INVITES_KEY,
    queryFn: listOrgInvites,
    staleTime: STALE_MS,
    enabled: opts.enabled ?? true,
  });
}

export function useInviteOrgMember() {
  const qc = useQueryClient();
  return useMutation<InviteMemberResponse, Error, InviteMemberRequest>({
    mutationFn: inviteOrgMember,
    onSuccess: () => {
      // The BE returns one of two shapes — either a member row or a
      // pending invite — and the user may toggle the role across calls,
      // so the safe play is to invalidate both queries and refetch.
      qc.invalidateQueries({ queryKey: MEMBERS_KEY });
      qc.invalidateQueries({ queryKey: INVITES_KEY });
    },
  });
}

export interface UpdateOrgMemberRoleArgs {
  readonly userId: string;
  readonly role: OrgRole;
}

export function useUpdateOrgMemberRole() {
  const qc = useQueryClient();
  return useMutation<OrgMember, Error, UpdateOrgMemberRoleArgs>({
    mutationFn: ({ userId, role }) => updateOrgMemberRole(userId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEMBERS_KEY });
    },
  });
}

/**
 * Note: confirmation lives upstream in the panel (typed-confirm
 * AlertDialog) — this hook just fires the DELETE.
 */
export function useRemoveOrgMember() {
  const qc = useQueryClient();
  return useMutation<{ readonly status: "removed" }, Error, string>({
    mutationFn: (userId) => removeOrgMember(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEMBERS_KEY });
    },
  });
}

export function useCancelOrgInvite() {
  const qc = useQueryClient();
  return useMutation<{ readonly status: "cancelled" }, Error, string>({
    mutationFn: (inviteId) => cancelOrgInvite(inviteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INVITES_KEY });
    },
  });
}
